import { CalendarDate, DateValue, isSameDay, isSameMonth } from '@internationalized/date';
import { BorderRadius, Color, Typography } from '@persona-id/design-tokens';
import { useRef } from 'react';
import { useCalendarCell } from 'react-aria';
import { CalendarState, RangeCalendarState } from 'react-stately';
import styled, { css } from 'styled-components';

const CalendarCellContainer = styled.td`
  text-align: center;
  width: 38px;
  height: 38px;
  padding: 0px;
`;

interface CalendarCellButtonProps {
  isSelected?: boolean;
  isSelectionStart?: boolean;
  isSelectionEnd?: boolean;
  isInvalid?: boolean;
  isDisabled?: boolean;
}

const CalendarCellButton = styled.button<CalendarCellButtonProps>`
  ${Typography.TextMedium};
  border-style: none;
  padding: 0;
  background: none;
  width: 100%;
  height: 100%;
  cursor: pointer;

  ${({ isSelected }) =>
    isSelected &&
    css`
      background: ${Color.CorePurple100};
    `}

  ${({ isSelectionStart }) =>
    isSelectionStart &&
    css`
      background: ${Color.BackgroundButtonPrimary};
      color: ${Color.TextInverse};
      border-top-left-radius: ${BorderRadius.Sm};
      border-bottom-left-radius: ${BorderRadius.Sm};
    `}

  ${({ isSelectionEnd }) =>
    isSelectionEnd &&
    css`
      background: ${Color.BackgroundButtonPrimary};
      color: ${Color.TextInverse};
      border-top-right-radius: ${BorderRadius.Sm};
      border-bottom-right-radius: ${BorderRadius.Sm};
    `}

  ${({ isDisabled }) =>
    isDisabled &&
    css`
      color: ${Color.CoreSlate300};
    `}

  ${({ isInvalid }) =>
    isInvalid &&
    css`
      background: ${Color.BackgroundButtonDanger};
      color: ${Color.TextInverse};
    `}
`;

interface CalendarCellProps {
  state: CalendarState | RangeCalendarState;
  date: CalendarDate;
  currentMonth: DateValue;
}

const CalendarCell = ({ state, date, currentMonth }: CalendarCellProps) => {
  const ref = useRef();
  const { cellProps, buttonProps, isSelected, isInvalid, isDisabled, formattedDate } =
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    useCalendarCell({ date }, state, ref);

  const highlightedRange = 'highlightedRange' in state && state.highlightedRange;

  const isOutsideMonth = !isSameMonth(currentMonth, date);
  const isSelectionStart = highlightedRange ? isSameDay(date, highlightedRange.start) : isSelected;
  const isSelectionEnd = highlightedRange ? isSameDay(date, highlightedRange.end) : isSelected;

  return (
    <CalendarCellContainer {...cellProps}>
      <CalendarCellButton
        {...buttonProps}
        hidden={isOutsideMonth}
        isSelected={isSelected}
        isSelectionStart={isSelectionStart}
        isSelectionEnd={isSelectionEnd}
        isInvalid={isInvalid}
        isDisabled={isDisabled}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        ref={ref}
      >
        {formattedDate}
      </CalendarCellButton>
    </CalendarCellContainer>
  );
};

export default CalendarCell;
